// frontend/src/components/TypingEffect.js
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Custom components for ReactMarkdown
const markdownComponents = {
  // Ensure paragraphs have proper spacing and text wrapping
  p: ({ children }) => (
    <p style={{ 
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      lineHeight: '1.5'
    }}>
      {children}
    </p>
  ),
  
  // Handle bullet points with better spacing
  ul: ({ children }) => (
    <ul style={{ 
      marginBottom: '1rem', 
      paddingLeft: '1.5rem',
      listStyle: 'disc'
    }}>
      {children}
    </ul>
  ),
  
  // Handle numbered lists with proper formatting
  ol: ({ children }) => (
    <ol style={{ 
      marginBottom: '1rem', 
      paddingLeft: '1.5rem',
      listStyle: 'decimal',
      counterReset: 'item'
    }}>
      {children}
    </ol>
  ),
  
  // Improve list item formatting
  li: ({ children, ordered }) => {
    // Check if this is the start of a numbered item (e.g. "1. ", "2. ")
    const isNumberedItem = typeof children === 'string' && /^\d+\.\s/.test(children);
    
    // If it's already a properly formatted list item, render normally
    if (!isNumberedItem) {
      return (
        <li style={{ 
          marginBottom: '0.75rem',
          lineHeight: '1.5'
        }}>
          {children}
        </li>
      );
    }
    
    // For text that looks like a numbered list but isn't properly formatted,
    // convert it to proper list formatting
    const content = children.replace(/^\d+\.\s/, '');
    return (
      <li style={{ 
        marginBottom: '0.75rem',
        lineHeight: '1.5',
        display: 'list-item'
      }}>
        {content}
      </li>
    );
  },
  
  // Handle line breaks
  br: () => <br />,
  
  // Handle emphasis without using **
  strong: ({ children }) => (
    <span style={{ fontWeight: 'bold' }}>{children}</span>
  ),
  em: ({ children }) => (
    <span style={{ fontStyle: 'italic' }}>{children}</span>
  )
};

const TypingEffect = ({ content, typingSpeed = 10, markdownEnabled = true }) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const contentRef = useRef(content);
  const indexRef = useRef(0);
  const timerRef = useRef(null);
  
  // Reset typing if content changes
  useEffect(() => {
    // Check if content has changed
    if (content !== contentRef.current) {
      contentRef.current = content;
      indexRef.current = 0;
      setDisplayedContent('');
      setIsComplete(false);
    }
  }, [content]);
  
  // Handle typing effect
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // If typing is complete, don't set a new timer
    if (isComplete) return;
    
    // Function to add the next character
    const typeNextCharacter = () => {
      // If we've typed all characters, mark as complete
      if (indexRef.current >= contentRef.current.length) {
        setIsComplete(true);
        clearInterval(timerRef.current);
        return;
      }
      
      // Add the next character to displayed content
      setDisplayedContent(prevContent => {
        // Make sure we're starting from the right position
        if (prevContent.length !== indexRef.current) {
          console.warn('Typing effect index mismatch. Resetting.');
          indexRef.current = prevContent.length;
        }
        
        // Get the next character
        const nextChar = contentRef.current.charAt(indexRef.current);
        indexRef.current++;
        
        // Return updated content
        return prevContent + nextChar;
      });
    };
    
    // Set interval for typing
    timerRef.current = setInterval(typeNextCharacter, typingSpeed);
    
    // Clean up timer on unmount or when content changes
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [typingSpeed, isComplete]);
  
  // Create placeholder for consistent height
  const placeholderStyle = {
    visibility: 'hidden',
    position: 'absolute',
    zIndex: -1,
    top: 0,
    left: 0,
    width: '100%'
  };
  
  // Render content
  return (
    <div className="typing-effect-container">
      {/* Invisible placeholder to maintain consistent height */}
      <div style={placeholderStyle}>
        {markdownEnabled ? (
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {contentRef.current}
          </ReactMarkdown>
        ) : (
          <div>{contentRef.current}</div>
        )}
      </div>
      
      {/* Visible typing content */}
      <div className="typing-effect">
        {markdownEnabled ? (
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {displayedContent}
          </ReactMarkdown>
        ) : (
          <div>{displayedContent}</div>
        )}
      </div>
    </div>
  );
};

export default TypingEffect;